
import React from 'react';
import { ChartDataResponseResult, GenericDataType, t, validateNonEmpty } from '@superset-ui/core';
import {
  ControlPanelConfig,
  sections,
  sharedControls,
  ControlStateMapping,
  ControlPanelsContainerProps,
} from '@superset-ui/chart-controls';
import { DEFAULT_FORM_DATA } from './types';
import { legendSection } from '../../../controls';
import { countryOptions } from '../data/countries';
import { commonControlsConfig } from '../utils';

const { showLabels } = DEFAULT_FORM_DATA;

const chartLegendSection = [...legendSection];
chartLegendSection.splice(2, 1);

const config: ControlPanelConfig = {
  controlPanelSections: [
    {
      ...sections.legacyRegularTime,
      expanded: false
    },
    {
      label: t('Query'),
      expanded: true,
      controlSetRows: [
        [
          {
            name: 'select_country',
            config: {
              type: 'SelectControl',
              label: t('Country'),
              default: null,
              choices: countryOptions,
              description: t('Which country to plot the map for?'),
              validators: [validateNonEmpty],
            },
          },
        ],
        [
          {
            name: 'enable_drill_down',
            config: {
              type: 'CheckboxControl',
              label: t('Enable DrillDown'),
              renderTrigger: false,
              default: DEFAULT_FORM_DATA.enableDrillDown,
              description: t('To enable selection of multiple columns for DrillDown Query.'),
            },
          },
        ],
        [
          {
            name: 'groupby',
            config: {
              ...sharedControls.groupby,
              label: t('Region Column'),
              description: t(
                'Column containing names/ISO 3166-2 codes of region/province/department in your table.',
              ),
              multi: false,
              clearable: false,
              validators: [validateNonEmpty]
            }
          }
        ],
        [
          {
            name: 'drill_down_groupby',
            config: {
              ...sharedControls.groupby,
              multi: true,
              label: t('Drilldown Columns'),
              description: t('Column values to use as categories for each level.'),
              visibility: ({ controls }: ControlPanelsContainerProps) =>
                Boolean(controls?.enable_drill_down?.value),
            },
          },
        ],
        [
          {
            name: 'showDbhdsRegion',
            config: {
              type: 'CheckboxControl',
              label: t('Show DBHDS Region'),
              renderTrigger: false,
              default: false,
              description: t('Whether to indicate its a DBHDS View')
            }
          },
        ],
        [
          {
            name: 'dbhdsRegion',
            config: {
              ...sharedControls.groupby,
              multi: false,
              label: t('DBHDS Region Columns'),
              description: t('Column values to use as categories for each level.'),
              visibility: ({ controls }: ControlPanelsContainerProps) =>
                Boolean(controls?.showDbhdsRegion?.value),
            },
          },
        ],
        ['metrics'],
        ['adhoc_filters'],
      ],
    },
    {
      label: t('Chart Options'),
      tabOverride: 'customize',
      expanded: false,
      controlSetRows: [
        //  ['color_scheme'],
        ...chartLegendSection,
        // eslint-disable-next-line react/jsx-key
        [<div className="section-header">{t('Labels')}</div>],
        [
          {
            name: 'show_labels',
            config: {
              type: 'CheckboxControl',
              label: t('Show Labels'),
              renderTrigger: true,
              default: showLabels,
              description: t('Whether to display the labels.'),
            },
          },
        ],
        [
          {
            name: 'tooltipFormat',
            config: {
              ...commonControlsConfig.tooltipFormat,
              default: DEFAULT_FORM_DATA.tooltipFormat,
            },
          }
        ],
        [
          {
            name: 'enableFiltering',
            config: {
              type: 'CheckboxControl',
              label: t('Enable Filtering'),
              renderTrigger: true,
              default: true,
              description: t('Whether to apply a filter on selecting a region.'),
            },
          },
        ],
        [
          {
            name: 'showGoldStandard',
            config: {
              type: 'CheckboxControl',
              label: t('Show Gold Standard'),
              renderTrigger: true,
              default: false,
              description: t('Whether to show gold standard based on the condition')
            }
          },
          {
            name: 'goldStandardCondition',
            config: {
              ...sharedControls.adhoc_filters,
              label: t('Condition for Gold Standard'),
              visibility: ({ controls }: ControlPanelsContainerProps) =>
                Boolean(controls?.showGoldStandard?.value),
            }
          }
        ],
        [
          {
            name: 'column_config',
            config: {
              type: 'ColumnConfigControl',
              label: t('Customize columns'),
              description: t('Further customize how to display each column'),
              renderTrigger: true,
              shouldMapStateToProps() {
                return true;
              },
              mapStateToProps(explore, _, chart) {
                let queryResponse = {};
                const columnToGenericDataType: { [key: string]: GenericDataType } = {};
                explore.datasource?.columns
                  .forEach((column: any) => {
                    columnToGenericDataType[column.column_name] = column.type_generic;
                  })
                if (chart?.queriesResponse?.[0] !== undefined) {
                  const chartDataResponseResult = chart?.queriesResponse?.[0] as ChartDataResponseResult;
                  const colnames = [...chartDataResponseResult.colnames];
                  const coltypes = [...chartDataResponseResult.coltypes];
                  const { enablePopUpDrilldown } = chart.latestQueryFormData;
                  if (enablePopUpDrilldown) {
                    const { popUpMetrics, popUpGroupBy } = chart.latestQueryFormData;
                    popUpGroupBy?.forEach((col: string) => {
                      if (!colnames.includes(col)) {
                        colnames.push(col);
                        coltypes.push(columnToGenericDataType[col]);
                      }
                    });
                    popUpMetrics?.forEach((metric: any) => {
                      if (!colnames.includes(metric.label)) {
                        colnames.push(metric.label);
                        coltypes.push(GenericDataType.NUMERIC);
                      }
                    });
                  }
                  queryResponse = {
                    colnames, coltypes
                  };
                }
                return {
                  queryResponse: queryResponse as ChartDataResponseResult,
                  emitFilter: explore?.controls?.table_filter?.value,
                };
              },
            },
          },
        ]
      ],
    },
    {
      label: t('PopUp Options'),
      expanded: false,
      controlSetRows: [
        [
          {
            name: 'enablePopUpDrilldown',
            config: {
              type: 'CheckboxControl',
              label: t('Show Pop Up Drill down'),
              renderTrigger: true,
              default: false,
              description: t('Whether to display the pop up drill down table on click'),
            },
          }
        ],
        [
          {
            name: 'popUpGroupBy',
            config: {
              ...sharedControls.groupby,
              label: t('Dimensions for PopUp Drill down'),
              visibility: ({ controls }: ControlPanelsContainerProps) =>
                Boolean(controls?.enablePopUpDrilldown?.value)
            }
          }
        ],
        [
          {
            name: 'popUpDropdownColumns',
            config: {
              ...sharedControls.groupby,
              label: t('Dropdown columns for PopUp Table'),
              visibility: ({ controls }: ControlPanelsContainerProps) =>
                Boolean(controls?.enablePopUpDrilldown?.value)
            }
          }
        ],
        [
          {
            name: 'popUpMetrics',
            config: {
              ...sharedControls.metrics,
              label: t('Metrics for PopUp Drill down'),
              validators: [],
              visibility: ({ controls }: ControlPanelsContainerProps) =>
                Boolean(controls?.enablePopUpDrilldown?.value)
            }
          }
        ]
      ]
    }
  ],
  onInit(state: ControlStateMapping) {
    return {
      ...state,
    };
  },
  denormalizeFormData: formData => ({
    ...formData,
    metric: formData.standardizedFormData.standardizedState.metrics[0],
    groupby: formData.standardizedFormData.standardizedState.columns,
  }),
  updateStandardizedState: (prevState, currState) => ({
    ...currState,
    metrics: [currState.metrics[0], ...prevState.metrics.slice(1)],
  }),
};

export default config;
